'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { IconSearch } from '@tabler/icons-react';
import { useTranslations } from 'next-intl';
import { Button } from './ui/button';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { GlobalSearchDialog, SEARCH_HASH } from '@/features/search';

interface ModKey {
  symbol: string;
  sr: string;
}

const MAC_MOD: ModKey = { symbol: '⌘', sr: 'Command' };
const OTHER_MOD: ModKey = { symbol: 'Ctrl', sr: 'Control' };

// Apple platforms use ⌘ for the shortcut; everything else uses Ctrl. Plain
// substring tests rather than a regex: the check is nothing more than "does
// the UA mention one of these four", and an unanchored alternation is an
// easy thing to get subtly wrong (CWE-185) for no benefit here.
const APPLE_PLATFORM_TOKENS = ['mac', 'iphone', 'ipad', 'ipod'];

function detectModKey(): ModKey {
  const ua = (
    navigator.userAgent +
    ' ' +
    (navigator.platform || '')
  ).toLowerCase();
  return APPLE_PLATFORM_TOKENS.some((token) => ua.includes(token))
    ? MAC_MOD
    : OTHER_MOD;
}

// useSyncExternalStore-friendly: server snapshot stays null so SSR and
// the first client render agree; React reconciles to the real value on
// the second client render without triggering a hydration mismatch.
const subscribe = () => () => {};

/**
 * Topbar trigger for system-wide search (OMA-548). Cmd+K (macOS) or
 * Ctrl+K (Windows/Linux) opens GlobalSearchDialog, which spans
 * documents/containers/OCR/pipeline data via /api/v1/search. The kbar
 * nav palette keeps its keyboard entry via Cmd+Shift+K.
 */
export default function SearchInput() {
  const t = useTranslations('common');
  // #search — global command palette open-state in the URL hash (also Cmd/Ctrl+K).
  const { open, onOpenChange: setOpen } = useHashDialog(SEARCH_HASH.search);
  const modKey = useSyncExternalStore<ModKey | null>(
    subscribe,
    detectModKey,
    () => null
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // Cmd/Ctrl+Shift+K is kbar's nav palette — keep that for kbar.
        if (e.shiftKey) return;
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  return (
    <div className='w-full space-y-2'>
      <Button
        variant='outline'
        className='bg-background text-muted-foreground relative h-9 w-full justify-start rounded-[0.5rem] text-sm font-normal shadow-none sm:pe-12 md:w-40 lg:w-64'
        onClick={() => setOpen(true)}
      >
        <IconSearch className='me-2 h-4 w-4' />
        {t('search')}
        {modKey && (
          <kbd
            aria-label={`${modKey.sr}+K`}
            className='bg-muted pointer-events-none absolute end-[0.3rem] top-[0.3rem] hidden h-6 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100 select-none sm:flex'
          >
            {modKey.symbol === '⌘' ? (
              <>
                <span className='text-xs'>⌘</span>K
              </>
            ) : (
              <span>Ctrl + K</span>
            )}
          </kbd>
        )}
      </Button>
      <GlobalSearchDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
